import config from '../../config/config';

import deleteRequest from './base/delete';
import getRequest from './base/get';
import patchRequest from './base/patch';
import postRequest from './base/post';

interface QueryParameters {
  limit?: number;
  [key: string]: any;
}

export default {
  /**
   * Activity logs for end consumer
   * @param {any} id id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchActivityLogs: (id: string, query: QueryParameters = {}) => {
    const parameters = { ...query };
    return getRequest(
      `${config.notificationApiUrl}api/super-admin/v1/general-log/consumerActivityLog/${id}`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch the Accounts list with pagination
   * @returns {any} promise
   */
  fetchConsumersFilterList: (): Promise<any> =>
    getRequest(`${config.subscription.accountApiUrl}api/super-admin/v1/consumer/list-filters`),

  /**
   * Fetching personal chargers history
   * @param {any} id - The ID of the personal charger
   * @returns {any} A promise resolving to the charger history
   */
  personalChargerHistory: (id: string | undefined): Promise<any> => {
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-transaction/consumer-transaction-history/${id}`,
    );
  },
  /**
   * Fetch the Total Consumer Card
   * @param {any} query query
   * @returns {any} promise
   */
  fetchTotalConsumerCard: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/end-consumer/total-end-consumer-cards`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch the Total Consumer Card
   * @param {any} query query
   * @returns {any} promise
   */
  fetchTotalChartApi: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/end-consumer/total-revenue-power-consumed-session-reports`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetch the Basic Information
   * @param {any} userCustomId user custom id
   * @returns {any} promise
   */
  fetchBasicInformation: (userCustomId: string): Promise<any> => {
    return getRequest(`${config.emsp.DriversApiUrl}api/emsp/v1/drivers/${userCustomId}`, {});
  },

  /**
   * Fetch the Revenue Power Session
   * @param {any} userCustomId user custom id
   * @param {any} data data
   * @returns {any} promise
   */
  fetchRevenuePowerSessions: (userCustomId: string, data: any): Promise<any> => {
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/end-consumer/revenue-power-sessions-by-user/${userCustomId}`,
      {
        ...data,
      },
    );
  },
  /**
   * Fetch the Basic Information
   * @param {any} userCustomId user custom id
   * @returns {any} promise
   */
  fetchNotifications: (userCustomId: string): Promise<any> => {
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/end-consumer/notifications/${userCustomId}`,
    );
  },

  /**
   * Fetch the Schedules list with pagination
   * @param {any} userCustomId user custom id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchSchedules: (userCustomId: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/end-consumer/personal-schedules/${userCustomId}`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch personal schedules Export Data
   * @param {any} userCustomId user custom id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchSchedulesExportData: (userCustomId: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/end-consumer/personal-schedules-export/${userCustomId}`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetch personal schedules Upcoming filters
   * @param {any} userCustomId user custom id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchSchedulesUpcomingFilters: (
    userCustomId: string,
    query: QueryParameters = {},
  ): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/end-consumer/personal-schedules-filter/${userCustomId}`,
      {
        ...parameters,
        status: 'upcoming',
      },
    );
  },

  /**
   * Fetch personal schedules Completed filters
   * @param {any} userCustomId user custom id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchSchedulesCompletedFilters: (
    userCustomId: string,
    query: QueryParameters = {},
  ): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/end-consumer/personal-schedules-filter/${userCustomId}`,
      {
        ...parameters,
        status: 'completed',
      },
    );
  },

  /**
   * Fetch the Reservations list with pagination
   * @param {any} userCustomId user custom id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchReservationsList: (userCustomId: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/end-consumer/commercial-schedules/${userCustomId}`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch commercial schedules filters
   * @param {any} userCustomId user custom id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchReservationsFilters: (userCustomId: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/end-consumer/commercial-schedules-filter/${userCustomId}`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetch the Reservations list with pagination
   * @param {any} userCustomId user custom id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchChargeSessionList: (userCustomId: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/end-consumer/get-charging-transactions/${userCustomId}`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetch commercial charge session filters
   * @param {any} userCustomId user custom id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchChargeSessionCommercialFilters: (
    userCustomId: string,
    query: QueryParameters = {},
  ): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/end-consumer/get-charging-transactions-filter/${userCustomId}`,
      {
        ...parameters,
        type: 'commercial',
      },
    );
  },

  /**
   * Fetch personal charge session filters
   * @param {any} userCustomId user custom id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchChargeSessionPersonalFilters: (
    userCustomId: string,
    query: QueryParameters = {},
  ): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/end-consumer/get-charging-transactions-filter/${userCustomId}`,
      {
        ...parameters,
        type: 'personal',
      },
    );
  },

  /**
   * Fetch the Guest Free list with pagination
   * @param {any} userCustomId user custom id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchGuestFreeChargingList: (userCustomId: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/end-consumer/get-guest-free-charging-schedules/${userCustomId}`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch guest free charging filters
   * @param {any} userCustomId user custom id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchGuestFreeChargingFilters: (
    userCustomId: string,
    query: QueryParameters = {},
  ): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/end-consumer/get-guest-free-charging-schedules-filter/${userCustomId}`,
      {
        ...parameters,
        type: 'personal',
      },
    );
  },

  /**
   * Fetch the Guest Free list with pagination
   * @param {any} userCustomId user custom id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchTransactionsPaymentList: (
    userCustomId: string,
    query: QueryParameters = {},
  ): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/end-consumer/get-end-consumer-payment-transactions/${userCustomId}`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetch transactions payment filters
   * @param {any} userCustomId user custom id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchTransactionsPaymentFilters: (
    userCustomId: string,
    query: QueryParameters = {},
  ): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/end-consumer/get-end-consumer-payment-transactions-filter/${userCustomId}`,
      {
        ...parameters,
        type: 'personal',
      },
    );
  },

  /**
   * Fetch the Vehicle list with pagination
   * @param {string} userCustomId - The custom ID of the user
   * @param {object} query - The query parameters
   * @returns {any} - The response from the API
   */
  fetchVehicleList: (userCustomId: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/end-consumer/get-end-consumer-vehicle-list/${userCustomId}`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Set the primary vehicle
   * @param {any} userCustomId - The custom ID of the user
   * @param {any} query - The query parameters
   * @returns {any} The response from the API
   */
  setVehiclePrimary: (userCustomId: string, query: QueryParameters = {}): Promise<any> => {
    return patchRequest(
      `${config.emsp.DriversApiUrl}api/emsp/v1/drivers/vehicles/${userCustomId}/${query.vehicleId}/select`,
      {},
    );
  },
  /**
   * Delete the vehicle
   * @param {any} userCustomId - The custom ID of the user
   * @param {any} vehicleId - The ID of the vehicle to delete
   * @returns {any} The response from the API
   */
  deleteVehicle: (userCustomId: string, vehicleId: string): Promise<any> => {
    return deleteRequest(
      `${config.emsp.DriversApiUrl}api/emsp/v1/drivers/vehicles/${userCustomId}/${vehicleId}`,
    );
  },

  /**
   * Fetch the Basic Information
   * @param {any} userCustomId user custom id
   * @returns {any} promise
   */
  fetchFavoriteStations: (userCustomId: string): Promise<any> => {
    return getRequest(
      `${config.emsp.DriversApiUrl}api/emsp/v1/drivers/favourite-stations/${userCustomId}`,
    );
  },
  /**
   * Fetch the Basic Information
   * @param {any} userCustomId user custom id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchPersonalChargers: (userCustomId: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/end-consumer/get-end-consumer-personal-chargers/${userCustomId}`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetch the Basic Information
   * @param {any} userCustomId user custom id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchWalletHistory: (userCustomId: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.emsp.DriversApiUrl}api/emsp/v1/drivers/wallet-history/${userCustomId}`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch the Basic Information
   * @param {any} userCustomId user custom id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchSessionList: (userCustomId: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/end-consumer/get-end-consumer-charging-session-list/${userCustomId}`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch the Payment Cards list with pagination
   * @param {any} userCustomId - The custom ID of the user
   * @param {any} query - The query parameters
   * @returns {any} - The response from the API
   */
  fetchPaymentMethods: (userCustomId: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/end-consumer/get-end-consumer-payment-methods/${userCustomId}`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Set the primary payment method
   * @param {any} userCustomId - The custom ID of the user
   * @param {any} query - The query parameters
   * @returns {any} - The response from the API
   */
  setPrimaryPaymentMethod: (userCustomId: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return patchRequest(
      `${config.emsp.DriversApiUrl}api/emsp/v1/drivers/payment-method/${userCustomId}`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch charging session info
   * @param {any} id id
   * @returns {any} promise
   */
  fetchChargingTransaction: (id: string): Promise<any> => {
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-transaction/charging-session-info/${id}`,
    );
  },
  /**
   * Charging session export
   * @param {any} userCustomId - The custom ID of the user
   * @param {any} query - The query parameters
   * @returns {any} - The response from the API
   */
  chargingSessionExport: (userCustomId: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/end-consumer/get-charging-transactions-export/${userCustomId}`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Reservation export
   * @param {any} userCustomId - The custom ID of the user
   * @param {any} query - The query parameters
   * @returns {any} - The response from the API
   */
  reservationExport: (userCustomId: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/end-consumer/commercial-schedules-export/${userCustomId}`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Guest charging export
   * @param {any} userCustomId - The custom ID of the user
   * @param {any} query - The query parameters
   * @returns {any} - The response from the API
   */
  guestChargingExport: (userCustomId: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/end-consumer/get-guest-free-charging-schedules-export/${userCustomId}`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Transaction export
   * @param {any} userCustomId - The custom ID of the user
   * @param {any} query - The query parameters
   * @returns {any} - The response from the API
   */
  transactionExport: (userCustomId: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/end-consumer/get-end-consumer-payment-transactions-export/${userCustomId}`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Delete the payment method
   * @param {any} userCustomId - The custom ID of the user
   * @param {any} cardId - The ID of the card
   * @returns {any} - The response from the API
   */
  deletePaymentMethod: (userCustomId: string, cardId: string): Promise<any> => {
    return deleteRequest(
      `${config.emsp.DriversApiUrl}api/emsp/v1/drivers/payment-method/${userCustomId}/${cardId}`,
    );
  },

  /**
   * Fetch payment details side panel
   * @param {any} userCustomId user custom id
   * @returns {any} promise
   */
  fetchPaymentDetailsSidePanel: (userCustomId: string): Promise<any> => {
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/end-consumer/get-end-consumer-transaction-details/${userCustomId}`,
    );
  },

  /**
   * Edit basic information
   * @param {any} userCustomId user custom id
   * @param {any} query query
   * @returns {any} promise
   */
  editBasicInformation: (userCustomId: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return patchRequest(`${config.emsp.DriversApiUrl}api/emsp/v1/drivers/${userCustomId}`, {
      ...parameters,
    });
  },
  /**
   * Fetch active session
   * @param {any} userCustomId user custom id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchActiveSession: (userCustomId: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.emsp.sessionsApiUrl}api/emsp/v2/commercial-charging-transaction/${userCustomId}/live`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Remote stop transaction
   * @param {any} query query
   * @returns {any} promise
   */
  remoteStopTransaction: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return postRequest(`${config.ocppApiUrl}api/ocpp16/v1/super-admin/remote-stop-transaction`, {
      ...parameters,
    });
  },

  /**
   * Fetch maintenance schedule API for export
   * @param {any} query - The query parameters
   * @returns {any} A promise that resolves with the exported data
   */
  fetchCommercialTransactionExport: (query: QueryParameters = {}) => {
    const parameters = { ...query };
    return getRequest(`${config.emsp.transactionApiUrl}api/emsp/v1/cdrs/export`, {
      ...parameters,
    });
  },

  /**
   * Fetch session count for vehicles
   * @param {string[] | { vehicleId?: string[]; 'vehicleId[]'?: string[] }} vehicleIds - Array of vehicle IDs or query object
   * @returns {Promise<any>} A promise that resolves to the vehicles session count response
   */
  fetchVehiclesSessionCount: (
    vehicleIds: string[] | { vehicleId?: string[]; 'vehicleId[]'?: string[] },
  ): Promise<any> => {
    let ids: string[] = [];
    if (Array.isArray(vehicleIds)) {
      ids = vehicleIds;
    } else if (vehicleIds && Array.isArray(vehicleIds.vehicleId)) {
      ids = vehicleIds.vehicleId;
    } else if (vehicleIds && Array.isArray(vehicleIds['vehicleId[]'])) {
      ids = vehicleIds['vehicleId[]'];
    }
    const query = ids.map((id) => `vehicleId[]=${encodeURIComponent(id)}`).join('&');
    return getRequest(
      `${config.emsp.sessionsApiUrl}api/emsp/v2/commercial-charging-transaction/vehicles-session-count${
        query ? `?${query}` : ''
      }`,
    );
  },
};
